import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { WorkOrderChangeObjectItem, WorkOrderChangeObjectItemFilterInput } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { getWorkOrderChangeObjects } from '../WorkOrderChangeQueries';

interface IUseWorkOrderChangeObjects {
    workOrderChangeId: number;
    withEffect?: boolean;
    resetOnUnmount?: boolean;
}

const requestId = 'workOrderChangeObjects';

export const useWorkOrderChangeObjects = (props: IUseWorkOrderChangeObjects) => {
    const dispatch = useDispatch();

    const { workOrderChangeId, withEffect = true, resetOnUnmount = true } = props;

    const variables = useMemo(() => {
        const where: WorkOrderChangeObjectItemFilterInput = {
            or: [{ isRemovedObject: { eq: null } }, { isRemovedObject: { eq: false } }],
        };

        return { workOrderChangeId, where };
    }, [workOrderChangeId]);

    const [workOrderChangeObjectsData, workOrderChangeObjectsStatus, , workOrderChangeObjectsRequest] = useRequest({
        requestId,
        query: getWorkOrderChangeObjects,
        variables,
        withEffect,
    });

    const [reset] = useResetStoreData({
        requestId,
    });

    const refreshWorkOrderChangeObjects = useCallback(
        (onSuccess?: () => void) => {
            dispatch(workOrderChangeObjectsRequest(variables, onSuccess));
        },
        [dispatch, variables, workOrderChangeObjectsRequest],
    );

    const patchedItems = useMemo(() => {
        if (!workOrderChangeObjectsData?.uerWorkOrderChangeObjectItems?.items) {
            return [];
        }

        const { items } = workOrderChangeObjectsData.uerWorkOrderChangeObjectItems;

        return items.map((obj: WorkOrderChangeObjectItem) => ({
            ...obj,
            parentId: obj.contractObject?.parentId,
            rootObjectId: obj.contractObject?.parentId,
            originParentId: obj.parentId,
            originRootObjectId: obj.rootObjectId,
        }));
    }, [workOrderChangeObjectsData?.uerWorkOrderChangeObjectItems]);

    useEffect(
        () => () => {
            if (resetOnUnmount) {
                dispatch(reset(undefined));
            }
        },
        [dispatch, reset, resetOnUnmount, workOrderChangeId],
    );

    return {
        workOrderChangeObjectsData: workOrderChangeObjectsData?.uerWorkOrderChangeObjectItems?.items ?? [],
        workOrderChangeObjectsDataPatched: patchedItems,
        workOrderChangeObjectsStatus,
        refreshWorkOrderChangeObjects,
    };
};
